import React from "react";
import { Phone, Copy } from "@phosphor-icons/react";
import { useToast } from "../../contexts/ToastContext";
import { formatPhoneIL } from "../../utils/phoneUtils";
import "./PhoneNumber.css";

/**
 * Displays an Israeli phone number in local format ("050-3337372") with a small
 * copy button next to it. By default the number is a `tel:` link (uses the raw
 * international value so dialing still works).
 *
 * Props:
 *  - phone: raw phone value (international or local)
 *  - className: class applied to the inner link/span (keeps existing styling)
 *  - iconSize: phone + copy icon size (default 14)
 *  - stopPropagation: stop click bubbling (for phones inside clickable cards)
 *  - noLink: render plain text instead of a tel: link
 *  - showIcon: show the leading phone icon (default true)
 */
function PhoneNumber({
  phone,
  className = "",
  iconSize = 14,
  stopPropagation = false,
  noLink = false,
  showIcon = true,
}) {
  const { showSuccess, showError } = useToast();

  if (!phone) return null;

  const formatted = formatPhoneIL(phone);

  const handleCopy = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!navigator.clipboard?.writeText) {
      showError("העתקה אינה נתמכת בדפדפן זה");
      return;
    }
    navigator.clipboard
      .writeText(formatted)
      .then(() => showSuccess("מספר הטלפון הועתק"))
      .catch((error) => {
        console.error("[CK-5501] Failed to copy phone number", {
          errorCode: "CK-5501",
          errorMessage: error?.message,
          timestamp: new Date().toISOString(),
        });
        showError("שגיאה בהעתקת המספר");
      });
  };

  const inner = (
    <>
      {showIcon && <Phone size={iconSize} />}
      <span className="phone-number-text" dir="ltr">
        {formatted}
      </span>
    </>
  );

  return (
    <span className="phone-number">
      {noLink ? (
        <span className={className}>{inner}</span>
      ) : (
        <a
          href={`tel:${phone}`}
          className={className}
          onClick={stopPropagation ? (e) => e.stopPropagation() : undefined}
        >
          {inner}
        </a>
      )}
      <button
        type="button"
        className="phone-number-copy"
        onClick={handleCopy}
        title="העתק מספר טלפון"
        aria-label="העתק מספר טלפון"
      >
        <Copy size={iconSize} />
      </button>
    </span>
  );
}

export default PhoneNumber;
